import { BrewManApiError, BrewManError } from "@/app/errors";
import Guid from "@/app/types/common/guid";
import { tracer } from "@/app/tracing";
import { computed, reactive, readonly } from "vue";
import { useI18n } from "@/plugins/i18n";
import type { SnackbarConfiguration } from "@/components/controls/snackbar/SnackbarTypes";
import { EnumSnackbarType } from "@/components/controls/snackbar/SnackbarTypes";
import environment from "@/environment";
import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import type { Framework } from "vuetify";

const { $m } = useI18n();

interface AppStoreState {
  version: string;
  contextualHelpLink: string;
  loading: boolean;
  entryCount: number;
  error: string | Error | null;
  unauthorisedError: EnumUserPermission | null;
  appStateKey: string;
  documentTitle: string;
  snackbarConfigs: SnackbarConfiguration[];
  theme: "light" | "dark";
}

const state = reactive<AppStoreState>({
  version: "1",
  contextualHelpLink: "",
  loading: false,
  entryCount: 0,
  error: null,
  appStateKey: Guid.create().toString(),
  documentTitle: $m.default_document_title,
  snackbarConfigs: [],
  unauthorisedError: null,
  theme: "light",
});

const localStorageThemeKey = "app-theme";

function setLoading(loading: boolean): void {
  if (loading) {
    tracer.pushSpan(false, "loading", { initiator: "loading" });
  } else {
    tracer.popSpan({ initiator: "loading" });
  }
  state.entryCount = state.entryCount + (loading ? 1 : -1);
  state.loading = state.entryCount > 0;
}

/* eslint-disable no-console */
function showError(error: string | Error | null, info?: string): void {
  // Special case for null or string
  if (error === null || error instanceof String) {
    state.error = error;
    return;
  }
  // Logging to the console allows Chrome to do it's thang with sourcemaps
  if (window.console && console.error) {
    console.error(error);
    console.log({ info });
  }

  // Do not present user with unauthenticated or no license errors
  if (error instanceof BrewManApiError) {
    if (error.isUnauthenticated) {
      return;
    }

    if (error.isPermissionDenied && error.reasonCode === "NoLicense") {
      return;
    }

    if (error.isPermissionDenied && error.reasonCode === "NoPermission") {
      // The message is the permission name
      const matchedError = EnumUserPermission.getValues().find(
        (permission) => (error.message.match(permission.value)?.length ?? 0) > 0
      );

      if (matchedError) {
        state.unauthorisedError = matchedError;
        return;
      }
    }
  }

  // Don't bother with failed to fetch errors
  if (error instanceof BrewManError) {
    if (error.message.includes("Failed to fetch")) {
      return;
    }
  }

  state.error = error;
}

/* eslint-enable no-console */

function clearUnauthorisedError() {
  state.unauthorisedError = null;
}

function toggleDarkTheme(opts: { darkTheme: boolean; $vuetify: Framework }) {
  const { darkTheme, $vuetify } = opts;
  const theme = darkTheme ? "dark" : "light";

  $vuetify.theme.dark = darkTheme;
  document.querySelector("html")?.setAttribute("data-theme", theme);

  localStorage.setItem(localStorageThemeKey, theme);

  state.theme = theme;
}

function setThemeFromLocalStorage(opts: { $vuetify: Framework }) {
  const { $vuetify } = opts;
  const theme = localStorage.getItem(localStorageThemeKey);

  toggleDarkTheme({
    darkTheme: theme === "dark",
    $vuetify,
  });
}

export default {
  state: readonly(state),
  setLoading,
  pushSnackbarConfig(config: Partial<SnackbarConfiguration>): void {
    // Setup Defaults
    state.snackbarConfigs.push(
      Object.assign(
        {
          id: Guid.create(),
          text: "",
          timeout: 2000,
          position: "bottom",
          type: EnumSnackbarType.info,
        },
        config
      )
    );
  },
  removeSnackbarConfig() {
    state.snackbarConfigs.shift();
  },
  setContextualHelpLink: (contextualHelpLink: string | undefined): void => {
    state.contextualHelpLink = contextualHelpLink ?? "";
  },
  forceRefresh: () => {
    state.appStateKey = Guid.create().toString();
  },
  showError,
  clearUnauthorisedError,
  setDocumentTitle: (title: string | null): void => {
    state.documentTitle = title
      ? title + $m.default_document_title_postfix
      : $m.default_document_title;
  },
  /**
   * Gets the appropriate help link for the current page (either contextual or general).
   */
  resolvedHelpLink: computed(
    () => state.contextualHelpLink || environment.helpPagesUrl
  ),
  toggleDarkTheme,
  setThemeFromLocalStorage,
};

// Decorator for Telling AppStore to show loader
export function WithLoading<T extends Record<string, any>, R>(
  target: T,
  key: string | symbol,
  descriptor: TypedPropertyDescriptor<(...args: any[]) => R>
): void {
  const fn = descriptor.value as Function;
  descriptor.value = function (this: any, ...args) {
    let wasPromise = false;
    try {
      setLoading(true);

      let result = fn.apply(this, args);
      if (isPromise(result)) {
        wasPromise = true;
        result.finally(() => {
          setLoading(false);
        });
      }
      return result;
    } finally {
      if (!wasPromise) {
        setLoading(false);
      }
    }
  };
}

const isPromise = (p: any) => {
  return p && Object.prototype.toString.call(p) === "[object Promise]";
};
